import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfirmProvider } from './hooks/useConfirm.jsx';
import { ChatContainer } from './components/Chat/ChatContainer';
import { useAuth } from './hooks/useAuth.jsx';
import LandingPage from './pages/LandingPage.jsx';
import BillingPage from './pages/Billing/BillingPage.jsx';
import { BillingReturn } from './pages/BillingReturn.jsx';
import LoginPage from './pages/Auth/LoginPage.jsx';
import RegisterPage from './pages/Auth/RegisterPage.jsx';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage.jsx';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage.jsx';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage.jsx';
import AdminDashboard from './pages/Admin/AdminDashboard.jsx';
import PrivacidadePage from './pages/Legal/PrivacidadePage.jsx';
import TermosPage from './pages/Legal/TermosPage.jsx';
import PlanosPage from './pages/PlanosPage.jsx';
import CadernoPage from './pages/CadernoPage.jsx';
import TrilhasPage from './pages/TrilhasPage.jsx';
import TrailDetailPage from './pages/TrailDetailPage.jsx';
import LeituraPage from './pages/LeituraPage.jsx';
import LeituraDiaPage from './pages/LeituraDiaPage.jsx';
import InsightsPage from './pages/InsightsPage.jsx';
import HomePage from './pages/HomePage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';
import TermsAcceptanceModal from './components/Legal/TermsAcceptanceModal.jsx';
import OnboardingModal from './components/Onboarding/OnboardingModal.jsx';
import { CookieBanner } from './components/CookieBanner.jsx';
import { UpdateRequiredScreen, UpdateAvailableBanner } from './components/common/UpdateGate.jsx';
import { AppShell } from './components/layout/AppShell.jsx';
import { DEFAULT_AUTHENTICATED_ROUTE } from './config/routes.js';
import { useNativeShell } from './hooks/useNativeShell.js';
import { isNative } from './services/platform.js';

// Placeholder neutro enquanto a sessão é resolvida (AuthProvider.isLoading):
// os guards abaixo não podem decidir o redirecionamento antes disso.
const AuthLoading = () => <div className="min-h-screen bg-background" aria-busy="true" />;

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <AuthLoading />;
  return isAuthenticated ? children : <Navigate to="/login" state={{ from: location }} replace />;
};

// P23H: usuário autenticado em '/', '/login' ou '/register' é enviado para
// /inicio — essas páginas são só para visitantes anônimos.
const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <AuthLoading />;
  return isAuthenticated ? <Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace /> : children;
};

// A landing page (§0.4 de opus5_mobile.md) é conteúdo só-web por decisão de
// escopo: tem preços, comparativo de planos e o selo "Pagamentos processados
// pelo Stripe", que não deve aparecer no bundle nativo (Guideline 3.1.1 —
// menção a checkout fora da loja). No nativo, quem não está autenticado e
// chega em "/" vai direto para o login em vez de ver a landing de marketing.
const RootRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <AuthLoading />;
  if (isAuthenticated) return <Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />;
  if (isNative()) return <Navigate to="/login" replace />;
  return <LandingPage />;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <AuthLoading />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== 'admin') return <Navigate to={DEFAULT_AUTHENTICATED_ROUTE} />;
  return children;
};

const AppContent = () => {
  const { isAuthenticated, termsAcceptanceRequired, onboardingRequired } = useAuth();
  const location = useLocation();
  // Status bar, teclado, botão voltar do Android, revalidação de sessão ao
  // voltar do background, e gate de versão mínima. No-op completo na web.
  const { updateRequired, updateAvailable, storeUrl, dismissUpdateAvailable } = useNativeShell();

  if (updateRequired) {
    return <UpdateRequiredScreen storeUrl={storeUrl} />;
  }

  const isExemptRoute = [
    '/',
    '/privacidade',
    '/termos',
    '/planos',
    '/login',
    '/register',
    '/verify-email',
    '/forgot-password',
    '/reset-password',
  ].includes(location.pathname);

  const showModal = isAuthenticated && termsAcceptanceRequired && !isExemptRoute;
  // Onboarding só aparece depois que os termos (bloqueante) já foram aceitos.
  const showOnboarding = isAuthenticated && !termsAcceptanceRequired && onboardingRequired && !isExemptRoute;

  return (
    <>
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/privacidade" element={<PrivacidadePage />} />
        <Route path="/termos" element={<TermosPage />} />
        <Route path="/planos" element={<PlanosPage />} />
        <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
        <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/chat"
          element={
            <PrivateRoute>
              <ChatContainer />
            </PrivateRoute>
          }
        />
        <Route
          path="/inicio"
          element={
            <PrivateRoute>
              <AppShell><HomePage /></AppShell>
            </PrivateRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <PrivateRoute>
              <AppShell><BillingPage /></AppShell>
            </PrivateRoute>
          }
        />
        <Route
          path="/caderno"
          element={
            <PrivateRoute>
              <AppShell><CadernoPage /></AppShell>
            </PrivateRoute>
          }
        />
        <Route
          path="/trilhas"
          element={
            <PrivateRoute>
              <AppShell><TrilhasPage /></AppShell>
            </PrivateRoute>
          }
        />
        <Route
          path="/trilhas/:trailId"
          element={
            <PrivateRoute>
              <AppShell><TrailDetailPage /></AppShell>
            </PrivateRoute>
          }
        />
        <Route
          path="/leitura"
          element={
            <PrivateRoute>
              <AppShell><LeituraPage /></AppShell>
            </PrivateRoute>
          }
        />
        <Route
          path="/leitura/:enrollmentId/dia/:day"
          element={
            <PrivateRoute>
              <AppShell><LeituraDiaPage /></AppShell>
            </PrivateRoute>
          }
        />
        <Route
          path="/insights"
          element={
            <PrivateRoute>
              <AppShell><InsightsPage /></AppShell>
            </PrivateRoute>
          }
        />
        <Route
          path="/billing/return"
          element={
            <PrivateRoute>
              <BillingReturn />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {updateAvailable && <UpdateAvailableBanner storeUrl={storeUrl} onDismiss={dismissUpdateAvailable} />}
      {showModal && <TermsAcceptanceModal />}
      {showOnboarding && <OnboardingModal mode="forced" />}
      <CookieBanner />
    </>
  );
};

const App = () => {
  return (
    <ConfirmProvider>
      <AppContent />
    </ConfirmProvider>
  );
};

export default App;